import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiResponse, FormServiceInterface } from '@hutsix/ngxh6';
import { environment } from '../../../../../../environments/environment';
import { PermitViewGrantService } from '../../../services/permit-view-grant.service';

interface PermitResult {
    /** TLC's per-type 'what happens next' line, shown on the confirmation screen. */
    submitMessage?: string | null;
    viewGrant?: string;
    viewGrantExpiresAt?: number;
    permitNumber: string;
    status: string;
    /** Shown on the confirmation so the applicant can see where it went. */
    email?: string | null;
    partySize?: number;
    token: string;
}

@Component({
    selector: 'app-apply-recreation',
    templateUrl: './apply-recreation.component.html',
    styleUrls: ['./apply-recreation.component.scss'],
    standalone: false,
})
export class ApplyRecreationComponent implements OnInit {
    public result?: PermitResult | null;

    /**
     * Boat-access entry path, set by the finder wizard (?sites=boat). It narrows the form
     * to boat-access sites and drops the work/LTR permit-number requirement - the same
     * form, shaped by the server for a visitor arriving by boat.
     */
    public boatAccess = false;

    /** The recreation form config, with the boat-access query threaded onto api_get. */
    public formOpts?: any;

    constructor(
        @Inject('FormService') private formService: FormServiceInterface,
        private route: ActivatedRoute,
        private grants: PermitViewGrantService,
    ) {}

    ngOnInit(): void {
        this.boatAccess = this.route.snapshot.queryParamMap.get('sites') === 'boat';

        // Start from the registered 'recreation' form and, on the boat-access path, point
        // its GET at the narrowed variant so the site list and hidden permit-number field
        // come shaped from the server (mirrors how fishing shapes its form with ?ref).
        const base: any = this.formService.getForm('recreation');
        this.formOpts = this.boatAccess ? { ...base, api_get: `${base.api_get}?sites=boat` } : base;
    }

    /** Restates what the finder promised, so the form does not drop that context. */
    public get outcome(): string {
        return this.boatAccess
            ? 'For boat-access sites, you will not need a work or resident permit number. Your booking is checked against the site before the permit is issued.'
            : 'You will need your work or long-term resident permit number for camping and road sites. Your booking is checked against the site before the permit is issued.';
    }

    /**
     * The crumb goes back to the recreation CHOICE question (the three buttons), not the
     * outcome leaf this form was reached through - both the boat and the standard path
     * were picked there, so it is the page that lets the applicant change that choice.
     */
    /** Back to the info page this form came from - boat-access or camping/day-use. */
    public get previousRoute(): string {
        return this.boatAccess ? '/boat-access' : '/recreation-area';
    }

    public get pdfUrl(): string | null {
        return this.result ? `${environment.apiUrl}/api/public/permit/${this.result.token}/pdf` : null;
    }

    public onSubmitted(res: ApiResponse<PermitResult>): void {
        // Only switch to the success panel on a real success; a validation error
        // (no status returned) must leave the form in place to show its inline errors.
        if (res?.data?.status) {
            this.result = res.data;
            // They just gave us this email on the form - don't re-ask on the next click.
            this.grants.remember(res.data);
        }
    }
}
